import { LogLevel, LogLevelLookup } from "./LogLevel";

export interface ILogger {
  debug(message: string, data?: any): void;
  info(message: string, data?: any): void;
  warn(message: string, data?: any): void;
  error(message: string, data?: any): void;
}

/**
 * A class to hold globally defined log settings.
 * If you provide a custom ILogger, you must abide by these rules by yourself.
 */
export class LogSettings {
  public static timestampLogMessages: boolean = true;
  public static timestampData: boolean = true;
}

const prefixes = ["[", "(", "{"] as const;
function timestampMessage(msg: string) {
  return `[${new Date().toISOString()}]${
    prefixes.some((p) => msg.startsWith(p)) ? msg : ` ${msg}`
  }`;
}

/**
 * Timestamps the data with a .$timestamp property.
 * The $ prefix signifies internal properties
 */
function timestampData(data: any) {
  if (!data || typeof data !== "object") return;
  data.$timestamp = new Date().toISOString();
  return data;
}

function logMessage(msg: string) {
  if (!msg) return "";
  return LogSettings.timestampLogMessages ? timestampMessage(msg) : msg;
}

function logData(data: any) {
  return LogSettings.timestampData ? timestampData(data) : data;
}

export class DefaultConsoleLogger implements ILogger {
  debug(message: string, data?: any): void {
    console.debug(logMessage(message), logData(data));
  }
  info(message: string, data?: any): void {
    console.log(logMessage(message), logData(data));
  }
  warn(message: string, data?: any): void {
    console.warn(logMessage(message), logData(data));
  }
  error(message: string, data?: any): void {
    console.error(logMessage(message), logData(data));
  }
}

export type LogLine = { level: LogLevel; message: string; data?: any };

/**
 * Grabs all log lines and saves them in memory
 */
export class LogAggregator implements ILogger {
  constructor(public lines: Array<LogLine> = []) {}

  clear() {
    this.lines = [];
  }

  debug(message: string, data?: any): void {
    this.lines.push({
      level: "debug",
      message: logMessage(message),
      data: logData(data),
    });
  }

  info(message: string, data?: any): void {
    this.lines.push({
      level: "info",
      message: logMessage(message),
      data: logData(data),
    });
  }

  warn(message: string, data?: any): void {
    this.lines.push({
      level: "warn",
      message: logMessage(message),
      data: logData(data),
    });
  }

  error(message: string, data?: any): void {
    this.lines.push({
      level: "error",
      message: logMessage(message),
      data: logData(data),
    });
  }
}

/**
 * A super simple, easily extensible logging solution.
 * Default logger is simply console.log, but can easily be
 * extended with external loggers, such as a logline aggregator
 * for use with debugging in vitest.
 */
export class Log {
  public static level: LogLevel = "debug";

  /**
   * All of the loggers available for processing.
   *
   * Made public in case general manipulation is required.
   *
   * Defaults to using @see DefaultConsoleLogger, so it works out of the box.
   */
  public static loggers: Array<ILogger> = [new DefaultConsoleLogger()];

  static addLogger<T extends ILogger>(logger: T): T {
    Log.loggers.push(logger);
    return logger;
  }

  static debug(message: string, data?: any): void {
    if (LogLevelLookup.get(Log.level)! < LogLevelLookup.get("debug")!) return;

    for (let i = 0, l = Log.loggers.length; i < l; i++) {
      Log.loggers[i].debug(message, data);
    }
  }

  static info(message: string, data?: any): void {
    if (LogLevelLookup.get(Log.level)! < LogLevelLookup.get("info")!) return;

    for (let i = 0, l = Log.loggers.length; i < l; i++) {
      Log.loggers[i].info(message, data);
    }
  }

  static warn(message: string, data?: any): void {
    if (LogLevelLookup.get(Log.level)! < LogLevelLookup.get("warn")!) return;

    for (let i = 0, l = Log.loggers.length; i < l; i++) {
      Log.loggers[i].warn(message, data);
    }
  }

  static error(message: string, data?: any): void {
    if (LogLevelLookup.get(Log.level)! < LogLevelLookup.get("error")!) return;

    for (let i = 0, l = Log.loggers.length; i < l; i++) {
      Log.loggers[i].error(message, data);
    }
  }
}
